import React, { useState, useEffect } from 'react';
import { FaUniversity, FaLink, FaCity, FaGlobe, FaUserGraduate, FaUsers, FaUserTie, FaUser, FaCalendarAlt, FaCheckCircle, FaFileAlt, FaStar, FaMagic, FaEnvelope, FaLock, FaInfoCircle, FaGraduationCap, FaBuilding } from 'react-icons/fa';
import TipTapEditor from '../common/TipTapEditor';
import { useMutations } from '../../hooks/useMutations';
import { UniversityOpportunityService } from '../../services/UniversityOpportunityService';
import { useSnackBar } from '../../hooks/useSnackBar';

const UniversityOpportunityForm = ({ onSuccess, user, opportunity, isEdit = false }) => {
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    title: opportunity?.title || '',
    department: opportunity?.department || user?.department || '',
    university: opportunity?.university || user?.university || '',
    university_url: opportunity?.university_url || '',
    city:  user?.city || opportunity?.city || '',
    country:  user?.country ||opportunity?.country || '',
    closing_date: opportunity?.closing_date ? new Date(opportunity.closing_date * 1000).toISOString().split('T')[0] : '',
    open_to: opportunity?.open_to || '',
    supervisor: opportunity?.supervisor || '',
    supervisor_url: opportunity?.supervisor_url || '',
    type: opportunity?.type || 'PhD',
    fully_funded: opportunity?.fully_funded || false,
    description: opportunity?.description || '',
    status: opportunity?.status || 'pending',
    opportunity_url: opportunity?.opportunity_url || '',
    collect_resumes: opportunity?.collect_resumes || false
  });

  const { mutateAsync: createOpportunity, isLoading: saving } = useMutations(
    isEdit ? UniversityOpportunityService.updateOpportunity : UniversityOpportunityService.createOpportunity
  );
  
  const enqMsg = useSnackBar();

  // Update form data when user data changes
  useEffect(() => {
    if (user && !isEdit) {
      console.log('UniversityOpportunityForm - User data changed:', user);
      // Always update form data when user changes
      setFormData(prev => {
        const newData = {
          ...prev,
          department: user.department || prev.department,
          university: user.university || prev.university,
          city: user.city || prev.city,
          country: user.country || prev.country,
        };
        console.log('UniversityOpportunityForm - Updated form data:', newData);
        return newData;
      });
    }
  }, [user, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({
      ...prev,
      description: content
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const closingDateEpoch = formData?.closing_date
        ? Math.floor(
            Date.UTC(
              ...formData.closing_date
                .split("-")
                .map((v, i) => (i === 1 ? Number(v) - 1 : Number(v)))
            ) / 1000
          )
        : null;

      const closingDateString = closingDateEpoch ? closingDateEpoch.toString() : null;

      const res = await createOpportunity({
        ...formData,
        ...(isEdit && { id: opportunity.id }),
        closing_date: closingDateString,
        fully_funded: formData?.fully_funded === true,
      });

      if (res?.error) {
        throw new Error(res?.error);
      } else {
        enqMsg(res?.message || `Opportunity ${isEdit ? 'updated' : 'created'} successfully! 🎉`, "success");
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            title: '',
            department: user?.department || '',
            university: user?.university || '',
            university_url: '',
            city: user?.city || '',
            country: user?.country || '',
            closing_date: '',
            open_to: '',
            supervisor: '',
            supervisor_url: '',
            type: 'PhD',
            fully_funded: false,
            description: '',
            status: 'pending',
            opportunity_url: '',
            collect_resumes: false
          });
        }, 1000);

        // Notify parent component
        if (onSuccess) {
          onSuccess(res.opportunity);
        }
      }
      
    } catch (error) {
      enqMsg(error?.message || `Failed to ${isEdit ? 'update' : 'create'} opportunity. Please try again.`, "error");
    }
  };

  const fieldConfigs = [
    { name: "university", icon: FaUniversity, placeholder: "e.g., Stanford University" },
    { name: "title", icon: FaGraduationCap, placeholder: "e.g., PhD in Computer Science" },
    { name: "department", icon: FaBuilding, placeholder: "e.g., Computer Science Department" },
    { name: "supervisor", icon: FaUserTie, placeholder: "e.g., Dr. Jane Smith" },
    { name: "city", icon: FaCity, placeholder: "e.g., Stanford" },
    { name: "country", icon: FaGlobe, placeholder: "e.g., United States" },
    { name: "university_url", icon: FaLink, placeholder: "https://university.edu" },
    { name: "supervisor_url", icon: FaLink, placeholder: "https://supervisor.profile" },
    { name: "open_to", icon: FaUsers, placeholder: "e.g., International students" },
    { name: "closing_date", icon: FaCalendarAlt, placeholder: "2024-12-31" },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* University Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaUniversity className="h-5 w-5 text-[#DB4F40] mr-2" />
              University Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fieldConfigs.slice(0, 4).map((field) => (
                <div key={field.name} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.name.charAt(0).toUpperCase() + field.name.slice(1).replace('_', ' ')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <field.icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={field.name === 'closing_date' ? 'date' : 'text'}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                        focusedField === field.name 
                          ? 'border-[#DB4F40] shadow-lg shadow-[#DB4F40]/10' 
                          : 'border-gray-300'
                      }`}
                      placeholder={field.placeholder}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Location Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaGlobe className="h-5 w-5 text-[#DB4F40] mr-2" />
              Location Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fieldConfigs.slice(4, 6).map((field) => (
                <div key={field.name} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.name.charAt(0).toUpperCase() + field.name.slice(1).replace('_', ' ')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <field.icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                        focusedField === field.name 
                          ? 'border-[#DB4F40] shadow-lg shadow-[#DB4F40]/10' 
                          : 'border-gray-300'
                      }`}
                      placeholder={field.placeholder}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaInfoCircle className="h-5 w-5 text-[#DB4F40] mr-2" />
              Additional Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fieldConfigs.slice(6, 10).map((field) => (
                <div key={field.name} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.name.charAt(0).toUpperCase() + field.name.slice(1).replace('_', ' ')}
                    {field.name === 'closing_date' && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <field.icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={field.name === 'closing_date' ? 'date' : 'text'}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                        focusedField === field.name 
                          ? 'border-[#DB4F40] shadow-lg shadow-[#DB4F40]/10' 
                          : 'border-gray-300'
                      }`}
                      placeholder={field.placeholder}
                      required={field.name === 'closing_date'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Settings */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaEnvelope className="h-5 w-5 text-[#DB4F40] mr-2" />
              Application Settings
            </h3>
            
            <div className="space-y-6">
              {/* Opportunity URL Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Opportunity URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLink className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="url"
                    name="opportunity_url"
                    value={formData.opportunity_url}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('opportunity_url')}
                    onBlur={() => setFocusedField(null)}
                    className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                      focusedField === 'opportunity_url' 
                        ? 'border-[#DB4F40] shadow-lg shadow-[#DB4F40]/10' 
                        : 'border-gray-300'
                    }`}
                    placeholder="https://your-university.edu/phd-position"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Provide a direct link to your university's application page to divert traffic there
                </p>
              </div>

              {/* Resume Collection Checkbox */}
              <div className="space-y-3">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="collect_resumes"
                    name="collect_resumes"
                    checked={formData.collect_resumes}
                    onChange={handleChange}
                    className="h-4 w-4 text-[#DB4F40] focus:ring-[#DB4F40] border-gray-300 rounded mt-1"
                  />
                  <div className="ml-3">
                    <label htmlFor="collect_resumes" className="block text-sm font-medium text-gray-900">
                      Collect resumes through PhDScanner portal
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      When checked, applicants will submit their resumes directly through PhDScanner, and you can manage applications in your dashboard. 
                      When unchecked, applicants will be redirected to your university's application page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Funding Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaStar className="h-5 w-5 text-[#DB4F40] mr-2" />
              Funding Information
            </h3>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="fully_funded"
                name="fully_funded"
                checked={formData.fully_funded}
                onChange={handleChange}
                className="h-4 w-4 text-[#DB4F40] focus:ring-[#DB4F40] border-gray-300 rounded"
              />
              <label htmlFor="fully_funded" className="ml-2 block text-sm text-gray-900">
                This position is fully funded
              </label>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaFileAlt className="h-5 w-5 text-[#DB4F40] mr-2" />
              Position Description
            </h3>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-[#DB4F40] focus-within:border-transparent transition-all duration-200">
                <TipTapEditor
                  content={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Describe the PhD position, requirements, research areas, and any other relevant details..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setFormData({
                  title: '',
                  department: user?.department || '',
                  university: user?.university || '',
                  university_url: '',
                  city: user?.city || '',
                  country: user?.country || '',
                  closing_date: '',
                  open_to: '',
                  supervisor: '',
                  supervisor_url: '',
                  type: 'PhD',
                  fully_funded: false,
                  description: '',
                  status: 'pending',
                  opportunity_url: '',
                  collect_resumes: false
                });
              }}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-[#DB4F40] to-[#C64537] text-white rounded-lg hover:from-[#C64537] hover:to-[#B73E31] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#DB4F40] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:shadow-[#DB4F40]/25"
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  {isEdit ? 'Updating Opportunity...' : 'Creating Opportunity...'}
                </div>
              ) : (
                isEdit ? 'Update Opportunity' : 'Create Opportunity'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UniversityOpportunityForm;
